import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, AlertCircle, CheckCircle2, Play, RefreshCw, Mail } from "lucide-react";
import ShareScorecardDialog from "@/components/ShareScorecardDialog";

const SEV = {
  HIGH: { cls: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/5", icon: AlertCircle, label: "HIGH" },
  MED: { cls: "text-[#FFB000] border-[#FFB000]/40 bg-[#FFB000]/5", icon: AlertTriangle, label: "MEDIUM" },
  LOW: { cls: "text-[#FFB000] border-[#FFB000]/20 bg-[#FFB000]/5", icon: AlertTriangle, label: "LOW" },
  OK: { cls: "text-[#00E676] border-[#00E676]/40 bg-[#00E676]/5", icon: CheckCircle2, label: "OK" },
};

export default function XeroHygiene() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/xero-hygiene/latest");
      setData(r.data || null);
    } catch { setData(null); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/xero-hygiene/scan");
      setData(r.data);
      toast.success("Hygiene scan complete.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    }
    setRunning(false);
  };

  return (
    <div className="p-6 space-y-6" data-testid="hygiene-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · BOOKS HYGIENE</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter uppercase mt-1">Xero Hygiene Playbook</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Deterministic checks on your Xero books: duplicate contacts, stale AR,
            forgotten drafts, missing contact details, round-number anomalies,
            duplicate invoice numbers, and FX exposure. No LLM, just rules — fast and explainable.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <button
              onClick={() => setShareOpen(true)}
              data-testid="share-scorecard-btn"
              className="border border-zinc-700 px-4 py-3 font-mono text-[11px] uppercase tracking-widest hover:border-[#FFB000]/50 hover:text-[#FFB000] flex items-center gap-2"
            >
              <Mail className="w-3.5 h-3.5" />
              SHARE WITH ACCOUNTANT
            </button>
          )}
          <button
            onClick={run}
            disabled={running}
            data-testid="hygiene-run-btn"
            className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 flex items-center gap-2 disabled:opacity-50"
          >
            {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {running ? "SCANNING…" : data ? "RE-SCAN" : "RUN SCAN"}
          </button>
        </div>
      </div>

      <ShareScorecardDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        scanId={data?.id}
      />

      {loading && <div className="font-mono text-[11px] text-zinc-500">Loading…</div>}

      {!loading && !data && (
        <Panel title="// NO SCAN YET">
          <div className="p-8 text-center">
            <ShieldCheck className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
            <p className="font-mono text-[11px] text-zinc-500">
              Click RUN SCAN to run the hygiene playbook against your Xero data.
            </p>
            <p className="font-mono text-[10px] text-zinc-700 mt-2">
              (Requires at least one Xero sync — go to Data Sources → Sync Xero if you haven't yet.)
            </p>
          </div>
        </Panel>
      )}

      {!loading && data && (
        <>
          <Panel title="// OVERALL HYGIENE SCORE" meta={<span className="font-mono text-[9px] text-zinc-600">{new Date(data.created_at).toLocaleString()}</span>}>
            <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-1 border-r border-zinc-900 pr-4">
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">SCORE</div>
                <div className={`font-heading font-black text-6xl tracking-tighter mt-1 ${
                  data.overall_score >= 85 ? "text-[#00E676]" :
                  data.overall_score >= 60 ? "text-[#FFB000]" : "text-[#FF3B30]"
                }`}>
                  {data.overall_score}
                </div>
                <div className="font-mono text-[9px] text-zinc-600 mt-1">/ 100</div>
              </div>
              <div>
                <div className="font-mono text-[9px] text-zinc-500 uppercase tracking-widest">HIGH</div>
                <div className="font-mono text-3xl text-[#FF3B30] mt-1">{data.severity_counts.HIGH}</div>
              </div>
              <div>
                <div className="font-mono text-[9px] text-zinc-500 uppercase tracking-widest">MED / LOW</div>
                <div className="font-mono text-3xl text-[#FFB000] mt-1">{data.severity_counts.MED + data.severity_counts.LOW}</div>
              </div>
              <div>
                <div className="font-mono text-[9px] text-zinc-500 uppercase tracking-widest">OK</div>
                <div className="font-mono text-3xl text-[#00E676] mt-1">{data.severity_counts.OK}</div>
              </div>
            </div>
            <div className="px-5 pb-4 font-mono text-[10px] text-zinc-600">
              Scanned {data.invoices_scanned.toLocaleString()} invoice(s) · {data.contacts_scanned.toLocaleString()} contact(s)
            </div>
          </Panel>

          {data.checks.map((c) => {
            const sev = SEV[c.severity] || SEV.OK;
            const Icon = sev.icon;
            return (
              <Panel
                key={c.id}
                title={`// ${c.title.toUpperCase()}`}
                meta={<span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${sev.cls}`}>
                  <Icon className="w-3 h-3 inline mr-1 -mt-0.5" />{sev.label}
                </span>}
              >
                <div className="p-5 space-y-3" data-testid={`check-${c.id}`}>
                  <p className="text-sm text-zinc-300">{c.summary}</p>
                  {c.recommendation && (
                    <div className="border-l-2 border-[#FFB000]/50 pl-3 text-xs text-zinc-400 leading-relaxed">
                      <span className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] mr-2">ACTION</span>
                      {c.recommendation}
                    </div>
                  )}
                  {Array.isArray(c.findings) && c.findings.length > 0 && (
                    <div className="border border-zinc-900 bg-zinc-950/50 divide-y divide-zinc-900 mt-3">
                      {c.findings.slice(0, 8).map((f, i) => (
                        <div key={i} className="px-3 py-2 font-mono text-[11px] text-zinc-400 flex items-center gap-3 flex-wrap">
                          {Object.entries(f).slice(0, 5).map(([k, v]) => (
                            <span key={k}><span className="text-zinc-600">{k}=</span>{String(v ?? "—")}</span>
                          ))}
                        </div>
                      ))}
                      {c.findings.length > 8 && (
                        <div className="px-3 py-2 font-mono text-[10px] text-zinc-600">
                          + {c.findings.length - 8} more…
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Panel>
            );
          })}
        </>
      )}
    </div>
  );
}
